import React, { useEffect, useState } from 'react';
import { Mail, ArrowRight, Sun, Zap, Leaf, Gauge, Cpu, BatteryCharging } from 'lucide-react';
import SEO from './SEO';

/**
 * Standalone landing page for Astro Power Systems.
 * Rendered at /astropowersystems — no Shire Advisors chrome, completely
 * self-contained so the AstroPowerSystems.com domain can point here.
 */
const AstroPowerSystems = () => {
  const [scrollY, setScrollY] = useState(0);

  useEffect(() => {
    const onScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <>
      <SEO
        title="Astro Power Systems — Sustainable Solar & Localized Power"
        description="Astro Power Systems, Inc. — Texas-incorporated since 1993. Focused on the solar power arena and sustainable localized power conversions."
        path="/astropowersystems"
      />

      <div
        className="min-h-screen text-white overflow-x-hidden relative"
        style={{
          background:
            'radial-gradient(ellipse at top, #0b1430 0%, #050812 45%, #020309 100%)',
        }}
        data-testid="astro-power-systems-page"
      >
        {/* Animated background grid */}
        <div
          className="absolute inset-0 opacity-[0.12] pointer-events-none"
          style={{
            backgroundImage:
              'linear-gradient(rgba(56,189,248,0.35) 1px, transparent 1px), linear-gradient(90deg, rgba(56,189,248,0.35) 1px, transparent 1px)',
            backgroundSize: '48px 48px',
            maskImage:
              'radial-gradient(ellipse at center, black 30%, transparent 75%)',
            WebkitMaskImage:
              'radial-gradient(ellipse at center, black 30%, transparent 75%)',
          }}
        />

        {/* Floating gradient orbs */}
        <div
          className="absolute -top-20 -left-20 w-[560px] h-[560px] rounded-full blur-[120px] opacity-40 pointer-events-none"
          style={{
            background:
              'radial-gradient(circle, #2563eb 0%, transparent 70%)',
            transform: `translateY(${scrollY * 0.15}px)`,
          }}
        />
        <div
          className="absolute top-40 -right-40 w-[680px] h-[680px] rounded-full blur-[140px] opacity-30 pointer-events-none"
          style={{
            background:
              'radial-gradient(circle, #f59e0b 0%, transparent 70%)',
            transform: `translateY(${scrollY * -0.1}px)`,
          }}
        />
        <div
          className="absolute top-[1200px] left-1/2 -translate-x-1/2 w-[700px] h-[700px] rounded-full blur-[160px] opacity-25 pointer-events-none"
          style={{
            background:
              'radial-gradient(circle, #0ea5e9 0%, transparent 70%)',
          }}
        />

        {/* Top bar */}
        <header className="relative z-10 flex items-center justify-between px-6 md:px-12 py-6">
          <div className="flex items-center gap-3">
            <LogoMark className="w-9 h-9" />
            <span className="font-mono text-xs tracking-[0.3em] uppercase text-sky-300/80">
              Astro · Power · Systems
            </span>
          </div>
          <a
            href="#contact"
            className="hidden md:inline-flex items-center gap-2 px-5 py-2 rounded-full border border-sky-400/30 text-sky-200 hover:bg-sky-400/10 hover:border-sky-400/60 transition-all text-sm"
            data-testid="astro-header-contact"
          >
            Contact <ArrowRight className="w-3.5 h-3.5" />
          </a>
        </header>

        {/* HERO */}
        <section className="relative z-10 px-6 md:px-12 pt-12 md:pt-24 pb-28 max-w-7xl mx-auto">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-sky-400/10 border border-sky-400/30 mb-8 font-mono text-[11px] tracking-widest uppercase text-sky-300">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
            Est. 1993 · Texas · Online
          </div>

          <h1 className="font-extrabold tracking-tight leading-[0.95] text-5xl sm:text-6xl md:text-7xl lg:text-8xl mb-8">
            <span className="block text-white">Sustainable.</span>
            <span className="block bg-clip-text text-transparent bg-gradient-to-r from-sky-400 via-cyan-300 to-amber-300">
              Localized. Solar.
            </span>
          </h1>

          <p className="text-lg md:text-xl text-slate-300/90 max-w-2xl leading-relaxed mb-10">
            Astro Power Systems, Inc. engineers{' '}
            <span className="text-white font-semibold">next-generation solar
            installations</span> and <span className="text-white font-semibold">
            sustainable localized power conversion systems</span> for
            businesses building resilient, clean energy infrastructure.
          </p>

          <div className="flex flex-wrap gap-4">
            <a
              href="#contact"
              className="group inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-gradient-to-r from-sky-500 to-cyan-400 text-navy-950 font-semibold hover:shadow-[0_0_32px_rgba(56,189,248,0.55)] transition-all"
              data-testid="astro-hero-cta"
            >
              Start a conversation
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </a>
            <a
              href="#capabilities"
              className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full border border-white/15 text-white/80 hover:bg-white/5 hover:border-white/30 transition-all"
            >
              See capabilities
            </a>
          </div>

          {/* Stat strip */}
          <div className="mt-24 grid grid-cols-2 md:grid-cols-4 gap-px bg-white/5 rounded-2xl overflow-hidden border border-white/10">
            {[
              { label: 'Incorporated', value: '1993', sub: 'State of Texas' },
              { label: 'Focus', value: 'Solar', sub: 'Since 2020 pivot' },
              { label: 'Systems', value: 'Localized', sub: 'On-site conversion' },
              { label: 'Ethos', value: 'Sustainable', sub: 'Resilient · Clean' },
            ].map((s) => (
              <div key={s.label} className="bg-[#050812]/80 backdrop-blur px-6 py-6">
                <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-sky-400/70 mb-2">
                  {s.label}
                </div>
                <div className="text-3xl md:text-4xl font-bold bg-clip-text text-transparent bg-gradient-to-br from-white to-slate-400">
                  {s.value}
                </div>
                <div className="text-xs text-slate-400 mt-1">{s.sub}</div>
              </div>
            ))}
          </div>
        </section>

        {/* ABOUT */}
        <section className="relative z-10 px-6 md:px-12 py-20 max-w-5xl mx-auto">
          <div className="flex items-start gap-6">
            <div className="hidden md:block w-px flex-shrink-0 self-stretch bg-gradient-to-b from-sky-400 via-sky-400/40 to-transparent" />
            <div>
              <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-sky-400 mb-4">
                / about 01
              </div>
              <h2 className="text-3xl md:text-5xl font-bold mb-6 leading-tight">
                Three decades of power engineering,<br />
                <span className="text-sky-300">reinvented for the solar era.</span>
              </h2>
              <p className="text-slate-300 text-lg leading-relaxed mb-5">
                Astro Power Systems, Inc. was incorporated in{' '}
                <span className="text-white font-semibold">June 1993 in Texas</span> as
                an electrical service and distribution company. After building
                deep domain expertise in industrial power delivery, Astro
                divested from conventional electrical services in{' '}
                <span className="text-white font-semibold">2020</span> to focus on
                the frontier that matters most today.
              </p>
              <p className="text-slate-300 text-lg leading-relaxed italic">
                Astro is now focused exclusively on the solar power arena and
                sustainable localized power conversion — bringing decades of
                hard-won electrical expertise to the projects that are
                reshaping how the grid actually works.
              </p>
            </div>
          </div>
        </section>

        {/* CAPABILITIES */}
        <section
          id="capabilities"
          className="relative z-10 px-6 md:px-12 py-20 max-w-7xl mx-auto"
        >
          <div className="text-center mb-14">
            <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-sky-400 mb-3">
              / capabilities 02
            </div>
            <h2 className="text-3xl md:text-5xl font-bold">
              What we build.
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                icon: Sun,
                title: 'Solar Power Systems',
                body: 'End-to-end solar installations sized for commercial, industrial, and campus-scale sites. From feasibility to first light.',
                accent: 'from-amber-400/80 to-orange-500/80',
              },
              {
                icon: BatteryCharging,
                title: 'Localized Power Conversion',
                body: 'On-site DC/AC conversion, inversion, and distribution tuned to the load profile — not the grid operator.',
                accent: 'from-sky-400/80 to-cyan-400/80',
              },
              {
                icon: Leaf,
                title: 'Sustainable Integration',
                body: 'Clean-energy engineering with lifecycle thinking: sourcing, deployment, monitoring, and decommissioning.',
                accent: 'from-emerald-400/80 to-teal-400/80',
              },
              {
                icon: Gauge,
                title: 'Performance Monitoring',
                body: 'Telemetry-first design. Every system ships with granular observability so you know exactly what it\'s doing.',
                accent: 'from-fuchsia-400/80 to-pink-400/80',
              },
              {
                icon: Cpu,
                title: 'Power Electronics',
                body: 'Custom power electronics and controls where off-the-shelf doesn\'t cut it. 30+ years of domain experience.',
                accent: 'from-violet-400/80 to-indigo-400/80',
              },
              {
                icon: Zap,
                title: 'Resilience Engineering',
                body: 'Microgrid-ready architectures that keep the lights on when the grid can\'t — by design, not as an afterthought.',
                accent: 'from-yellow-400/80 to-amber-400/80',
              },
            ].map((c) => (
              <div
                key={c.title}
                className="group relative rounded-2xl border border-white/10 bg-white/[0.03] backdrop-blur-sm p-7 hover:border-sky-400/40 hover:bg-white/[0.06] transition-all duration-300 overflow-hidden"
              >
                <div
                  className={`absolute -top-10 -right-10 w-40 h-40 rounded-full blur-3xl opacity-20 bg-gradient-to-br ${c.accent} group-hover:opacity-40 transition-opacity`}
                />
                <div className={`inline-flex w-12 h-12 rounded-xl bg-gradient-to-br ${c.accent} items-center justify-center mb-5 shadow-lg`}>
                  <c.icon className="w-6 h-6 text-[#050812]" />
                </div>
                <h3 className="text-xl font-bold mb-2 text-white">{c.title}</h3>
                <p className="text-slate-400 text-sm leading-relaxed">{c.body}</p>
                <div className="mt-5 font-mono text-[10px] tracking-[0.2em] text-sky-400/60 uppercase">
                  ◆ Capability
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* CONTACT */}
        <section
          id="contact"
          className="relative z-10 px-6 md:px-12 py-24 max-w-5xl mx-auto"
        >
          <div className="relative rounded-3xl border border-sky-400/20 bg-gradient-to-br from-sky-500/[0.08] via-slate-900/60 to-[#050812] p-10 md:p-16 overflow-hidden">
            <div className="absolute inset-0 opacity-20 pointer-events-none" style={{
              backgroundImage:
                'linear-gradient(rgba(56,189,248,0.2) 1px, transparent 1px), linear-gradient(90deg, rgba(56,189,248,0.2) 1px, transparent 1px)',
              backgroundSize: '32px 32px',
            }} />
            <div className="relative">
              <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-sky-400 mb-4">
                / contact 03
              </div>
              <h2 className="text-3xl md:text-5xl font-bold mb-5 leading-tight">
                Power projects start with a conversation.
              </h2>
              <p className="text-slate-300 text-lg mb-8 max-w-xl leading-relaxed">
                Commercial solar, custom power electronics, or a localized
                conversion system — tell us what you&apos;re building and we&apos;ll
                figure out if we&apos;re a fit.
              </p>
              <div className="flex flex-wrap gap-4">
                <a
                  href="mailto:info@shireadvisors.com?subject=Astro%20Power%20Systems%20Inquiry"
                  className="group inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-gradient-to-r from-sky-500 to-cyan-400 text-[#050812] font-semibold hover:shadow-[0_0_32px_rgba(56,189,248,0.55)] transition-all"
                  data-testid="astro-contact-email"
                >
                  <Mail className="w-4 h-4" />
                  info@shireadvisors.com
                </a>
                <a
                  href="tel:+12146732710"
                  className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full border border-white/15 text-white/90 hover:bg-white/5 hover:border-white/30 transition-all font-mono"
                >
                  (214) 673-2710
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* FOOTER */}
        <footer className="relative z-10 border-t border-white/5 mt-10">
          <div className="max-w-7xl mx-auto px-6 md:px-12 py-10 flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-3">
              <LogoMark className="w-7 h-7" />
              <div>
                <div className="font-semibold text-sm text-white">Astro Power Systems, Inc.</div>
                <div className="text-[11px] text-slate-500 font-mono uppercase tracking-wider">Texas · Est. 1993</div>
              </div>
            </div>
            <div className="text-xs text-slate-500 text-center md:text-right">
              Represented by{' '}
              <a
                href="https://shireadvisors.com"
                className="text-sky-400 hover:text-sky-300 transition-colors"
                data-testid="astro-shire-link"
              >
                Shire Advisors, LLC
              </a>
              <div className="mt-1 font-mono">© {new Date().getFullYear()} · All rights reserved</div>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
};

/** Minimal halftone-dot logo mark inspired by the reference image. */
const LogoMark = ({ className = '' }) => (
  <svg
    viewBox="0 0 48 48"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={className}
    aria-label="Astro Power Systems logo"
  >
    <defs>
      <radialGradient id="apsDot" cx="50%" cy="50%" r="50%">
        <stop offset="0%" stopColor="#60a5fa" />
        <stop offset="100%" stopColor="#2563eb" />
      </radialGradient>
    </defs>
    {[
      [24, 8, 2], [18, 12, 2], [30, 12, 2],
      [12, 18, 2], [24, 18, 3], [36, 18, 2],
      [8, 24, 2], [18, 24, 3], [30, 24, 3], [40, 24, 2],
      [12, 30, 2], [24, 30, 3], [36, 30, 2],
      [18, 36, 2], [30, 36, 2], [24, 40, 2],
    ].map(([cx, cy, r], i) => (
      <circle key={i} cx={cx} cy={cy} r={r} fill="url(#apsDot)" />
    ))}
  </svg>
);

export default AstroPowerSystems;
